import { useEffect } from 'react'
import { Alert, App, Form, Input, Modal, Select, Tabs, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  CHANNELS,
  TEMPLATE_CODE,
  TEMPLATE_CODE_MAX,
  TEMPLATE_LOCALES,
  deleteTemplate,
  upsertTemplate,
  type Channel,
  type TemplateLocale,
  type TemplateView,
} from './notifyApi'

/** Bitta (code, channel) juftligi uchun uch tildagi yozuvlar — `NotifyTemplatesPage` guruhlaydi */
export interface TemplateGroup {
  code: string
  channel: Channel
  byLocale: Partial<Record<TemplateLocale, TemplateView>>
}

interface TemplateFormModalProps {
  open: boolean
  /** `null` = yangi (code, channel) juftligi */
  group: TemplateGroup | null
  onClose: () => void
}

type LocaleFields = Record<TemplateLocale, { subject: string; body: string }>

interface FormValues {
  code: string
  channel: Channel
  uz: { subject?: string; body?: string }
  ru: { subject?: string; body?: string }
  en: { subject?: string; body?: string }
}

function emptyLocaleFields(): LocaleFields {
  return {
    uz: { subject: '', body: '' },
    ru: { subject: '', body: '' },
    en: { subject: '', body: '' },
  }
}

/**
 * Shablon tahrirlagichi — UCH TIL BIR EKRANDA (task talabi).
 *
 * Backend kontrakti `(code, channel, locale)` bo'yicha ALOHIDA yozuv
 * (`TemplateController.upsert` — bitta chaqiruv, bitta til). Panel bu uchta
 * chaqiruvni bitta forma ortiga yashiradi: admin uchala tilni to'ldirib
 * "Saqlash" bossa, faqat TO'LDIRILGAN (bo'sh bo'lmagan `body`) tillar
 * yuboriladi, oldin mavjud bo'lib endi bo'shatilgan til esa O'CHIRILADI —
 * aks holda "tilni tozalab qo'ydim" amali jimgina e'tiborsiz qolardi.
 */
export function TemplateFormModal({ open, group, onClose }: TemplateFormModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const isEdit = group !== null

  useEffect(() => {
    if (!open) return
    const locales = emptyLocaleFields()
    if (group) {
      for (const locale of TEMPLATE_LOCALES) {
        const entry = group.byLocale[locale]
        if (entry) locales[locale] = { subject: entry.subject ?? '', body: entry.body }
      }
    }
    form.setFieldsValue({
      code: group?.code ?? '',
      channel: group?.channel ?? 'SMS',
      ...locales,
    })
  }, [open, group, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const code = values.code.trim()
      const channel = values.channel
      for (const locale of TEMPLATE_LOCALES) {
        const field = values[locale]
        /*
          ⚠️ `values[locale]` YO'Q BO'LISHI MUMKIN — X-SIB-015 ning ildizi
          shu edi (`field.body` → "Cannot read properties of undefined
          (reading 'body')"). Birinchi qatlam `onOk` da
          (`getFieldsValue(true)`), bu esa ikkinchisi.

          ⚠️ VA BU YERDA `?? { body: '' }` YOZISH MUMKIN EMAS. Bo'sh
          `body` quyida "til bo'shatildi" deb o'qiladi va mavjud
          shablonni O'CHIRADI — ya'ni yiqilishni ma'lumot yo'qotish
          bilan almashtirgan bo'lardik. Qiymat umuman yo'q bo'lishi
          "foydalanuvchi bu tilga TEGMAGAN" degani, "bo'shatdi" degani
          emas: to'g'ri javob — hech narsa qilmaslik.
        */
        if (field === undefined) continue
        const body = field.body?.trim() ?? ''
        const existing = group?.byLocale[locale] ?? null

        if (body === '') {
          // Bo'sh qoldirilgan — avval mavjud bo'lgan bo'lsa o'chiriladi,
          // aks holda hech narsa qilinmaydi (bo'sh yozuv yaratilmaydi)
          if (existing) await deleteTemplate(existing.id)
          continue
        }

        await upsertTemplate({
          code,
          channel,
          locale,
          subject: field.subject?.trim() || null,
          body,
        })
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.templates })
      // X-SIB-004 — oyna yopilgandan keyin ekranda hech qanday tasdiq
      // qolmasdi va operator amal o'tdimi yoki yo'qmi bilmasdi
      void message.success(isEdit ? t('common.toastSaved') : t('common.toastCreated'))
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin, keyingi ochilish toza boshlansin */
  const close = () => {
    form.resetFields()
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('notify.template.editTitle') : t('notify.template.createTitle')}
      onCancel={close}
      /*
        ⚠️ X-SIB-015 — `validateFields()` NATIJASI TO'LIQ EMAS.

        `validateFields()` qaytaradigan obyekt butun forma anbari emas:
        rc-field-form uni RO'YXATDAN O'TGAN (ya'ni DOM'da MOUNT bo'lgan)
        maydonlar bo'yicha yig'adi — `useForm.js` ichida
        `getFieldEntities(true).forEach(f => namePathList.push(...))`,
        keyin `getFieldsValue(namePathList)`.

        antd `Tabs` esa panellarni DANGASA chizadi: ochilmagan til
        yorlig'ining `Form.Item` lari umuman mount bo'lmaydi. Natijada
        admin faqat "uz" ni to'ldirib "Saqlash" bosganda `values.ru` va
        `values.en` UNDEFINED bo'lib kelardi va quyidagi sikl xom
        JavaScript xatosi bilan yiqilardi. Foydalanuvchi esa uni
        sahifadagi qizil bannerda ko'rardi — testerlar aynan shuni yozgan.

        `getFieldsValue(true)` ANBARNI TO'LIQ oladi, ya'ni `useEffect` da
        `setFieldsValue` bilan qo'yilgan uchala til ham keladi.
        Validatsiya baribir avval ishlaydi: `code` bo'sh bo'lsa saqlash
        boshlanmaydi.
      */
      onOk={() =>
        submitOnValid(form, () => save.mutate(form.getFieldsValue(true) as FormValues))
      }
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={640}
    >
      <Typography.Paragraph type="secondary">{t('notify.template.formHint')}</Typography.Paragraph>

      {/* Xato oynaning ICHIDA qoladi: ilgari u sahifa banneriga chiqardi va
          modal niqobi ortida operatorga umuman ko'rinmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {/*
          ⚠️ MAYDON DARAJASIDAGI SHAKL TEKSHIRUVI (tashqi tester, C6).

          Ilgari bu yerda FAQAT `required` turardi. Tekshiruvchi uchta
          yaroqsiz kod kiritdi va maydon ostida HECH QANDAY xato
          ko'rmadi: saqlash boshqa majburiy maydon tufayli to'xtardi,
          ya'ni `code` ning o'zi haqida panel bir og'iz ham gapirmasdi.
          Nuqsonning shakli shu — validatsiya YO'Q edi, ko'rinmas emas.

          ⚠️ NAQSH `TemplateResource.TemplateRequest#code` DAGI
          `@Pattern` BILAN AYNI. Ikkalasi kerak va sabab har xil:
          server — haqiqiy shartnoma (panel yagona mijoz emas), panel
          esa xatoni MAYDON YONIDA, so'rov ketishidan oldin aytadi.
          Faqat serverda qolsa operator 400 ni oynaning tepasidagi
          umumiy bannerda ko'rardi va qaysi maydon aybdorligini
          bilmasdi.

          ⚠️ Qiymat `trim()` dan KEYIN tekshiriladi: saqlashda ham
          `values.code.trim()` yuboriladi (yuqorida), ya'ni tekshiruv
          yuboriladigan AYNAN o'sha satrni ko'rishi kerak. Aks holda
          « balance-low » xom holda qizil bo'lardi, server esa uni
          bemalol qabul qilardi.
        */}
        <Form.Item
          name="code"
          label={t('notify.template.code')}
          extra={t('notify.template.codeHint')}
          rules={[
            { required: true, message: t('notify.template.codeRequired') },
            {
              validator: (_rule, value: unknown) => {
                if (typeof value !== 'string') return Promise.resolve()
                const code = value.trim()
                if (code === '') return Promise.resolve() // `required` aytadi
                if (code.length > TEMPLATE_CODE_MAX) {
                  return Promise.reject(new Error(t('notify.template.codeTooLong')))
                }
                if (!TEMPLATE_CODE.test(code)) {
                  return Promise.reject(new Error(t('notify.template.codeFormat')))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <Input autoFocus disabled={isEdit} placeholder="payment.reminder" />
        </Form.Item>

        <Form.Item
          name="channel"
          label={t('notify.channel')}
          rules={[{ required: true }]}
        >
          <Select disabled={isEdit} options={CHANNELS.map((c) => ({ value: c, label: t(`notify.channelName.${c}`) }))} />
        </Form.Item>

        <Tabs
          items={TEMPLATE_LOCALES.map((locale) => ({
            key: locale,
            label: t(`language.${locale}`),
            children: (
              <>
                <Form.Item name={[locale, 'subject']} label={t('notify.template.subject')} extra={t('notify.template.subjectHint')}>
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[locale, 'body']}
                  label={t('notify.template.body')}
                  /*
                    ⚠️ `placeholder` ATAYLAB uzatiladi va qiymati o'zi ham
                    `{{...}}` shaklida. Matn operatorga shablon SINTAKSISINI
                    ko'rsatadi (`TemplateRenderer` aynan `{{key}}` ni
                    almashtiradi), ya'ni qavslar ekranda TURISHI kerak.

                    Ilgari o'rin umuman uzatilmasdi va i18next uni
                    `skipOnVariables` bilan xom qoldirardi — ekranda
                    tasodifan to'g'ri matn chiqardi, lekin bu N2 dagi
                    (`sent {{n}} times`) nuqson bilan AYNI holat edi va
                    `tests/i18n-placeholders.mjs` ikkalasini ham ushlaydi.
                    Endi namuna ANIQ: `{{login}}`.
                  */
                  extra={t('notify.template.bodyHint', { placeholder: '{{login}}' })}
                >
                  <Input.TextArea rows={5} maxLength={2000} showCount />
                </Form.Item>
              </>
            ),
          }))}
        />
      </Form>
    </Modal>
  )
}
